// React
import { useState, useEffect } from "react";

// Hooks
import useExtensionWithFirebase from "./useExtensionWithFirebase";

// Logic
import { describePresence, ExtensionPresence, ExtensionWithFirebase } from "logic/extensionWithFirebase";

type WebotStatus = {
  /** A WhatsApp Web tab with the extension is ready to send. */
  appStatus: boolean;
  /** The first status has arrived. */
  dataReady: boolean;
  /** What to tell the realtor when not ready (Hebrew, with what to do), else null. */
  errorData: string | null;
  /** The account's status as its tabs report it. */
  presence: ExtensionPresence | null;
};

/**
 * One shared live status for every component on the page. It LISTENS to what the account's WhatsApp tabs
 * report (the extension writes when its state changes, and a lease every few minutes). Until 2026-10 this
 * created a database job every 4 seconds per open page and waited 15 s for an answer: 1.34M jobs in 13
 * days, half never answered, and any slow answer showed OFF over a working tab.
 */
class WebotStatusSingleton {
  private static instance: WebotStatusSingleton;
  private status: WebotStatus = { appStatus: false, dataReady: false, errorData: null, presence: null };
  private subscribers: Set<(status: WebotStatus) => void> = new Set();
  private extensionService: ExtensionWithFirebase | null = null;
  private stopListening: (() => void) | null = null;

  private constructor() {}

  public static getInstance(): WebotStatusSingleton {
    if (!WebotStatusSingleton.instance) {
      WebotStatusSingleton.instance = new WebotStatusSingleton();
    }
    return WebotStatusSingleton.instance;
  }

  public getStatus(): WebotStatus {
    return { ...this.status };
  }

  public subscribe(callback: (status: WebotStatus) => void): () => void {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
      if (this.subscribers.size === 0) {
        this.stop();
      }
    };
  }

  public start(extensionService: ExtensionWithFirebase | null): void {
    if (this.extensionService === extensionService && (this.stopListening || !extensionService)) return;
    this.stop();
    this.extensionService = extensionService;

    if (!extensionService) {
      this.update({ appStatus: false, dataReady: true, errorData: "שירות לא זמין", presence: null });
      return;
    }

    this.stopListening = extensionService.subscribeToPresence((presence) => {
      const ready = presence.state === "ready";
      this.update({ appStatus: ready, dataReady: true, errorData: ready ? null : describePresence(presence), presence });
    });
  }

  private stop(): void {
    this.stopListening?.();
    this.stopListening = null;
  }

  private update(status: WebotStatus): void {
    this.status = status;
    const copy = this.getStatus();
    this.subscribers.forEach((callback) => callback(copy));
  }
}

// The hook now uses the singleton to manage shared state
export function useWebotStatus(): WebotStatus {
  const [status, setStatus] = useState<WebotStatus>(() => WebotStatusSingleton.getInstance().getStatus());

  const extensionService = useExtensionWithFirebase();

  useEffect(() => {
    const statusManager = WebotStatusSingleton.getInstance();
    const unsubscribe = statusManager.subscribe(setStatus);
    statusManager.start(extensionService);
    setStatus(statusManager.getStatus());
    return unsubscribe;
  }, [extensionService]);

  return status;
}
